import React, { useState, useEffect, useRef } from 'react';
import { capitalizeFirstLetter, formatProfileTag } from '../../../../shared/utils/function';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '../../../../shared/components/form';
import HeaderSection from '../../../../components/layout/header';

const normalizeCategoryKey = (key) =>
  String(key).toLowerCase().replace(/\s*\/\s*/g, '/').replace(/\s+/g, ' ').trim();

/** Signup / settings: keep API order except these three fixed at the end */
const TAIL_CATEGORY_ORDER = ['genre/style', 'performer type', 'other'];

const getOrderedCategoryKeys = (categoriesObj) => {
  const keys = Object.keys(categoriesObj || {});
  if (keys.length === 0) return keys;

  const byNormalized = new Map();
  keys.forEach((k) => {
    byNormalized.set(normalizeCategoryKey(k), k);
  });

  const tailKeys = TAIL_CATEGORY_ORDER.map((n) => byNormalized.get(n)).filter(Boolean);
  const tailSet = new Set(tailKeys);
  const rest = keys.filter((k) => !tailSet.has(k));

  return [...rest, ...tailKeys];
};

const ProfileTags = ({
  form,
  title,
  buttonText,
  onContinue,
  onBack,
  categories = {},
  loading = false,
  saving = false
}) => {
  const navigate = useNavigation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [showLimitMessage, setShowLimitMessage] = useState(false);
  const limitMessageTimeoutRef = useRef(null);

  const formSelectedTags = form.watch('selectedTags') || [];

  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);

    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  // Hide limit message when count drops below 5 (user unselected a tag)
  useEffect(() => {
    if (formSelectedTags.length < 5) {
      if (limitMessageTimeoutRef.current) {
        clearTimeout(limitMessageTimeoutRef.current);
        limitMessageTimeoutRef.current = null;
      }
      setShowLimitMessage(false);
    }
  }, [formSelectedTags.length]);

  useEffect(() => {
    return () => {
      if (limitMessageTimeoutRef.current) clearTimeout(limitMessageTimeoutRef.current);
    };
  }, []);

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
    } else {
      navigate.toCreatorSignup();
    }
  };

  const handleTagToggle = (tag) => {
    const currentTags = form.getValues('selectedTags') || [];
    const isRemoving = currentTags.includes(tag);
    const wouldExceedLimit = !isRemoving && currentTags.length >= 5;

    if (wouldExceedLimit) {
      if (limitMessageTimeoutRef.current) clearTimeout(limitMessageTimeoutRef.current);
      setShowLimitMessage(true);
      limitMessageTimeoutRef.current = setTimeout(() => {
        setShowLimitMessage(false);
        limitMessageTimeoutRef.current = null;
      }, 3000);
      return;
    }

    const newTags = isRemoving
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];

    form.setValue('selectedTags', newTags, { shouldValidate: true, shouldDirty: true });
  };

  const handleRemoveTag = (tag) => {
    handleTagToggle(tag);
  };

  const isTagSelected = (tag) => formSelectedTags.includes(tag);
  const isTagLimitReached = formSelectedTags.length >= 5;

  const getFilteredTags = (tagArray) => {
    if (!searchQuery) return tagArray;
    return tagArray.filter((tag) =>
      tag.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // const getCategoryKeys = () => Object.keys(categories);
  const getCategoryKeys = () => getOrderedCategoryKeys(categories);

  const renderTagItem = (tag) => {
    const isSelected = isTagSelected(tag);
    const isDisabled = !isSelected && isTagLimitReached;
    return (
      <div
        key={tag}
        className={`profile-tags-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
        onClick={() => !isDisabled && handleTagToggle(tag)}
        style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
      >
        <div className="profile-tags-item-content">
          <span className="profile-tags-item-text">
            {formatProfileTag(tag)}
          </span>
        </div>
        <div className="profile-tags-radio">
          <div className={`profile-tags-radio-dot ${isSelected ? 'active' : ''}`} />
        </div>
      </div>
    );
  };

  return (
    <div className={`profile-tags-screen`}>
      {/* Header */}
      <HeaderSection
        type="text"
        title={title}
      />

      <main className="profile-tags-main">
        <div className="profile-tags-container">
          <Form {...form}>
            {/* Welcome Section */}
            <div className="profile-tags-welcome">
              <div className="profile-tags-icon">
                <i className="fas fa-tags" />
              </div>
              <p className="profile-tags-subtitle">
                Select at least one tag to boost your discoverability
              </p>
              <div className="profile-tags-divider" />
            </div>

            {/* Search Section */}
            <div className="profile-tags-search">
              <div className="profile-tags-search-container">
                <i className="profile-tags-search-icon fas fa-magnifying-glass" />
                <input
                  type="text"
                  placeholder="Search tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="profile-tags-search-input"
                />
              </div>
            </div>

            {loading ? (
              <div className="profile-tags-loading">
                <div className="loading-spinner" />
                <p>Loading categories...</p>
              </div>
            ) : (
              <>
                <FormField
                  control={form.control}
                  name="selectedTags"
                  render={({ field }) => (
                    <FormItem className="profile-tags-selected-section">
                      <FormLabel className="profile-tags-selected-title" style={{ fontSize: '14px' }}>
                        Selected Tags
                      </FormLabel>
                      <FormControl>
                        <div className="profile-tags-selected-container">
                          {formSelectedTags.length === 0 ? (
                            <div className="profile-tags-empty-state">No tags selected</div>
                          ) : (
                            formSelectedTags.map((tag) => (
                              <div key={tag} className="profile-tags-selected-tag">
                                <span className="profile-tags-selected-text">
                                  {formatProfileTag(tag)}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveTag(tag)}
                                  className="profile-tags-remove-btn"
                                >
                                  ×
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      </FormControl>
                      <FormMessage className="profile-tags-error-message" />
                    </FormItem>
                  )}
                />

                {/* Category Navigation */}
                <div className="profile-tags-categories">
                  <div className="profile-tags-category-buttons">
                    <button
                      onClick={() => setActiveCategory('all')}
                      className={`profile-tags-category-btn ${activeCategory === 'all' ? 'active' : ''}`}
                    >
                      All Tags
                    </button>
                    {getCategoryKeys().map((categoryName) => (
                      <button
                        key={categoryName}
                        onClick={() => setActiveCategory(categoryName)}
                        className={`profile-tags-category-btn ${activeCategory === categoryName ? 'active' : ''}`}
                      >
                        {capitalizeFirstLetter(categoryName)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="profile-tags-list">
                  {activeCategory === 'all' ? (
                    getCategoryKeys().map((categoryName) => {
                      const tagsArray = categories[categoryName];
                      const filteredTags = getFilteredTags(tagsArray);

                      if (filteredTags.length === 0) return null;

                      return (
                        <div key={categoryName} className="profile-tags-category-section">
                          <h2 className="profile-tags-category-title">
                            {capitalizeFirstLetter(categoryName)}
                          </h2>
                          <div className="profile-tags-grid">
                            {filteredTags.map(renderTagItem)}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    categories[activeCategory] && (
                      <div className="profile-tags-category-section">
                        <h2 className="profile-tags-category-title">
                          {capitalizeFirstLetter(activeCategory)}
                        </h2>
                        <div className="profile-tags-grid">
                          {getFilteredTags(categories[activeCategory]).map(renderTagItem)}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </>
            )}
          </Form>
        </div>
      </main>

      <footer className="profile-tags-footer">
        <div className="footerForSignup">
          <button
            onClick={() => onBack ? onBack() : navigate.toSignup()}
            className="profile-tags-back-footer-btn"
          >
            Back
          </button>
          <div className="profile-tags-footer-counter-wrap">
            {formSelectedTags.length >= 1 && (
              <>
                <span className="profile-tags-footer-counter">
                  {formSelectedTags.length}/5
                </span>
                {showLimitMessage && (
                  <span className="profile-tags-limit-helper">5 tags only</span>
                )}
              </>
            )}
          </div>
          <button
            onClick={handleContinue}
            disabled={formSelectedTags.length < 1 || saving || form.formState.isSubmitting}
            className={`profile-tags-continue-btn btn_dark_pink ${formSelectedTags.length < 1 || saving ? 'disabled' : ''}`}
          >
            {saving || form.formState.isSubmitting ? 'Saving...' : buttonText}
          </button>
        </div>
      </footer>
    </div>
  );
};

export default ProfileTags; 